// Third party
import React, { Component } from 'react';
import { SortingRule } from 'react-table';
import classNames from 'classnames';
import camelCase from 'lodash/camelCase';

// Components
import { IntegerValue } from '../common';
import { withErrorBoundary } from '../errors';
import { Tooltip } from '../tooltips';
import AdvancedSearchResult from './AdvancedSearchResult';
import { PrimaryButton, IconButton } from '../buttons';

// Utils
import { formatMessage, formatLocalizedString, FormattedMessage, formatNumber } from '../../../utils/i18n';
import { getHoldingPerformanceVal } from '../../../utils/advanced-search';
import { uniqueArray } from '../../../utils/objects';

// Types
import { BenchmarkSearchState } from '../../../redux/modules/types/benchmark-search';
import ComparisonBenchmark from '@/types/comparison-benchmark';

const roundValue =
  (digits: number) =>
  ({ value }: any) => <span>{value ? formatNumber(value, digits) : '-'}</span>;

interface Props {
  benchmarkState: BenchmarkSearchState;
  savedBenchmarks: ComparisonBenchmark[];
  onSortingChange: (sorting: SortingRule[]) => void;
  setSelectedBenchmarks: (benchmarks: ComparisonBenchmark[]) => void;
  loading?: boolean;
  insideModal?: boolean;
  onLoadMoreResults: () => void;
  hasMore: boolean;
  totalResultsCount?: number;
  useWindowScrollAncestor?: boolean;
  onToggleSaved: (benchmark: ComparisonBenchmark) => void;
  onStartComparison: () => void;
  onAddToPortfolio: () => void;
  hideSaveFund?: boolean;
}

class LegacyBenchmarkSearchResult extends Component<Props> {
  static defaultProps = {
    onSortingChange: () => {},
  };

  getDefaultColumns = () => {
    const { hideSaveFund } = this.props;

    return [
      {
        Header: () => <span>{formatMessage('Benchmarks.Table.Headers.BenchmarkName')}</span>,
        accessor: 'name',
        className: 'benchmark-name',
        minWidth: 150,
        Cell: ({ original, value }: { original: ComparisonBenchmark; value: string }) => (
          <Tooltip message={value}>
            <span>{value}</span>
          </Tooltip>
        ),
      },
      {
        Header: () => <span>{formatMessage('Benchmarks.Table.Headers.Classification')}</span>,
        accessor: 'classification',
        minWidth: 100,
        Cell: ({ value }: { value: string }) => (
          <Tooltip message={value}>
            <span>{value}</span>
          </Tooltip>
        ),
      },
      {
        Header: () => <span>{formatMessage('Benchmarks.Table.Headers.3Y')}</span>,
        accessor: 'performance.percentGrowth.3Y',
        Cell: ({ value }: { value: number }) => <IntegerValue value={value} growth />,
        width: 90,
      },
      {
        Header: () => <span>{formatMessage('Benchmarks.Table.Headers.5Y')}</span>,
        accessor: 'performance.percentGrowth.5Y',
        Cell: ({ value }: { value: number }) => <IntegerValue value={value} growth />,
        width: 90,
      },
      {
        Header: () => <span>{formatMessage('Benchmarks.Table.Headers.10Y')}</span>,
        accessor: 'performance.percentGrowth.10Y',
        Cell: ({ value }: { value: number }) => <IntegerValue value={value} growth />,
        width: 90,
      },
      {
        Header: () => <span>{formatMessage('Benchmarks.Table.Headers.YT')}</span>,
        accessor: 'performance.percentGrowth.YT',
        Cell: ({ value }: { value: number }) => <IntegerValue value={value} growth />,
        width: 120,
      },
      {
        Header: () => <span>{formatMessage('Benchmarks.Table.Headers.StdDev')}</span>,
        accessor: 'performance.standardDeviation',
        Cell: roundValue(2),
        width: 120,
      },
      {
        accessor: '_id',
        className: 'cell-icon',
        sortable: false,
        width: 48,
        includeInCompact: true,
        show: !hideSaveFund,
        Cell: ({ original }: { original: ComparisonBenchmark }) => {
          const classes = classNames('saved-benchmark-btn', {
            active: this.props.savedBenchmarks && this.props.savedBenchmarks.some(b => b._id === original._id),
          });
          return <IconButton className={classes} icon="star-full" onClick={() => this.props.onToggleSaved(original)} />;
        },
      },
      {
        accessor: 'isFocused',
        includeInCompact: true,
        className: 'focused-cell',
        width: 40,
        sortable: false,
        show: !!this.props.benchmarkState.focusedBenchmark,
        Cell: ({ value }: { value: boolean }) => (
          <div className="focused-arrow">
            <span>
              {value ? <i className="icon-cheveron-right" /> : null}
              {'\xa0'}
            </span>
          </div>
        ),
      },
    ];
  };

  formatData = (benchmarks?: ComparisonBenchmark[]) => {
    if (!benchmarks) {
      return [];
    }
    return benchmarks.map(benchmark => ({
      ...benchmark,
      name: formatLocalizedString(benchmark.name),
      classification: formatLocalizedString(benchmark.classification),
      performance: {
        ...benchmark.performance,
        standardDeviation: getHoldingPerformanceVal(benchmark, 'standardDeviation'),
      },
    }));
  };

  getActions = () => [
    <PrimaryButton key="action-add" compact icon="plus" onClick={this.props.onAddToPortfolio}>
      <FormattedMessage id="Search.Benchmarks.CreateCustom" />
    </PrimaryButton>,
    <PrimaryButton key="action-compare" compact icon="plus" onClick={this.props.onStartComparison}>
      <FormattedMessage id="Search.Benchmarks.AddToComparison" />
    </PrimaryButton>,
  ];

  onSelectItems = (ids: string[]) => {
    const {
      benchmarkState: { benchmarks, selectedBenchmarks },
    } = this.props;

    if (!benchmarks) {
      return;
    }

    const newBenchmarks = benchmarks.filter(b => ids.includes(b._id));
    const selected = uniqueArray([...selectedBenchmarks, ...newBenchmarks]);

    this.props.setSelectedBenchmarks(selected);
  };

  onUnselectItems = (ids: string[]) => {
    const {
      benchmarkState: { selectedBenchmarks },
    } = this.props;

    const items = selectedBenchmarks.filter(b => !ids.includes(b._id));

    this.props.setSelectedBenchmarks(items);
  };

  render() {
    const {
      benchmarkState: { selectedBenchmarks, benchmarks },
      insideModal,
      loading,
      totalResultsCount,
      useWindowScrollAncestor,
      hasMore,
    } = this.props;

    return (
      <AdvancedSearchResult
        classicVersionOnly // TODO in CONN-6933 + see note in column-definition/FundSearchResult.tsx
        className="fund-search-result"
        data={benchmarks && this.formatData(benchmarks)}
        selected={this.formatData(selectedBenchmarks)}
        columns={this.getDefaultColumns()}
        onSortingChange={this.props.onSortingChange}
        loading={loading}
        insideModal={insideModal}
        onLoadMoreResults={this.props.onLoadMoreResults}
        hasMore={hasMore}
        actions={this.getActions()}
        totalResultsCount={totalResultsCount}
        useWindowScrollAncestor={useWindowScrollAncestor}
        onSelectItems={this.onSelectItems}
        onUnselectItems={this.onUnselectItems}
        selectedTitle={formatMessage('Search.Benchmarks.SelectedTitle', { count: selectedBenchmarks.length })}
        labelAccessor="name"
      />
    );
  }
}

export default withErrorBoundary(LegacyBenchmarkSearchResult);
